/**
 * Группа уведомлений одной задачи в колокольчике.
 *
 * Пять событий по одной задаче подряд — пять строк с одинаковым
 * заголовком, и за ними не видно остальных задач. Группа сворачивает
 * их в одну строку: задача, последнее событие и число событий;
 * нажатие разворачивает всю серию.
 *
 * Группировка клиентская и только для колокольчика: в истории каждая
 * строка нужна отдельно — её фильтруют, отмечают и возвращают
 * в непрочитанные по одной.
 */

import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/shared/ui";

import type { NotificationItem } from "@taskflow/client-core/notifications";
import { CategoryIcon } from "./CategoryIcon";
import { NotificationRow } from "./NotificationRow";
import { headlineOf, summaryOf } from "./notificationText";

function eventsCount(count: number): string {
  const tens = count % 100;
  const units = count % 10;
  if (tens >= 11 && tens <= 14) return `${String(count)} событий`;
  if (units === 1) return `${String(count)} событие`;
  if (units >= 2 && units <= 4) return `${String(count)} события`;
  return `${String(count)} событий`;
}

interface NotificationGroupProps {
  items: NotificationItem[];
  onOpen: (item: NotificationItem) => void;
}

export function NotificationGroup({ items, onOpen }: NotificationGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();
  const latest = items[0] as NotificationItem;
  const unread = items.filter((item) => !item.read).length;

  return (
    <li className="border-b border-line/60 last:border-b-0">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        title={expanded ? "Свернуть" : "Показать все события по задаче"}
        onClick={() => setExpanded((value) => !value)}
        className={cn(
          "flex w-full gap-3 px-3 py-2.5 text-left transition-colors hover:bg-sunken/60",
          "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
          unread > 0 ? "bg-accent-soft/40" : "bg-transparent",
        )}
      >
        <CategoryIcon type={latest.type} className="mt-0.5 size-4 shrink-0 text-muted" />

        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-fg">
            {headlineOf(latest)}
          </span>
          <span className="mt-0.5 line-clamp-2 text-[13px] text-muted">{summaryOf(latest)}</span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[12px] text-faint">
            <span>{latest.createdAtLabel}</span>
            <span className="rounded-full bg-sunken px-1.5 py-px font-semibold text-muted">
              {eventsCount(items.length)}
            </span>
            {unread > 0 && <span className="font-semibold text-accent">непрочитанных: {unread}</span>}
            {latest.payload.boardName !== null && latest.payload.boardName !== undefined && (
              <span className="truncate">{latest.payload.boardName}</span>
            )}
          </span>
        </span>

        <ChevronDown
          aria-hidden
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted transition-transform",
            !expanded && "-rotate-90",
          )}
        />
      </button>

      <ul id={bodyId} hidden={!expanded} className="ml-5 border-t border-l border-line/60">
        {items.map((item) => (
          <NotificationRow key={item.id} item={item} compact onOpen={onOpen} />
        ))}
      </ul>
    </li>
  );
}
